import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Boxes, Loader2 } from 'lucide-react';
import { emitRefsChanged } from '../../lib/generationEvents';
import WhiteboxCutDialog from './WhiteboxCutDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * V15.01 — Maquette blanche déterministe : extrusion mathématique du tracé
 * (backend Python, 0 crédit, fidélité 100 %), ancrée comme référence d'étape 1.
 * `imageBase64` : capture Three.js (option haute qualité) envoyée à la place du rendu Python.
 */
export const createDeterministicWhitebox = async ({ projectId, lotId, vueAngle, imageBase64, peripheralCut, floorTints = true, doorStyle, backWallsFull = false, aspect = '1:1' }) => {
  const body = { vue_angle: vueAngle || 'Aérienne isométrique 45°', peripheral_cut: peripheralCut ?? 0.5, floor_tints: !!floorTints, back_walls_full: !!backWallsFull, aspect: aspect === '16:9' ? '16:9' : '1:1' };
  if (doorStyle) body.door_style = doorStyle;
  if (imageBase64) body.image_base64 = imageBase64;
  const { data } = await axios.post(`${API}/projects/${projectId}/lots/${lotId}/whitebox-deterministic`, body, { withCredentials: true, timeout: 60000 });
  emitRefsChanged({ lotId, stage: 'whitebox', anchored: !!data.anchored });
  return data;
};

export const DeterministicWhiteboxButton = ({ projectId, lotId, vueAngle, disabled, onDone, compact = false, className = '', testId = 'whitebox-deterministic-btn' }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const run = async (peripheralCut, floorTints, doorStyle, backWallsFull, aspect) => {
    if (!lotId) return toast.error('Choisis d\'abord un lot.');
    setBusy(true);
    try {
      const job = await createDeterministicWhitebox({ projectId, lotId, vueAngle, peripheralCut, floorTints, doorStyle, backWallsFull, aspect });
      setOpen(false);
      toast.success('Maquette blanche générée et ancrée', {
        description: `${job.render_meta?.walls ?? '?'} murs · ${job.render_meta?.openings ?? 0} ouvertures · ${job.render_meta?.width ?? ''}×${job.render_meta?.height ?? ''} · ${job.generation_time_seconds ?? 0} s · sans IA — étape 2 débloquée.`,
      });
      onDone?.(job);
    } catch (e) {
      toast.error('Maquette blanche impossible', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
    <button
      type="button"
      onClick={() => (lotId ? setOpen(true) : toast.error('Choisis d\'abord un lot.'))}
      disabled={busy || disabled}
      title="Choisir la coupe des façades, prévisualiser, puis extruder mathématiquement les murs tracés (HSP, ouvertures, escaliers, cloisons basses) en iso 45° — 0 crédit, géométrie exacte, ancré comme référence de l'étape 1."
      className={`inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${compact ? 'text-[11.5px] px-3 py-2' : 'text-[12px] px-4 py-2.5'} ${className}`}
      style={{ background: 'linear-gradient(135deg, #E8F1F8 0%, #C9D9E8 100%)', color: '#0B1220', border: '1px solid rgba(255,255,255,0.5)' }}
      data-testid={testId}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Boxes className="w-3.5 h-3.5" />}
      {busy ? 'Extrusion…' : 'Maquette blanche instantanée'}
      {!compact && <span className="text-[10px] font-normal opacity-70">· sans IA · 0 crédit</span>}
    </button>
    <WhiteboxCutDialog open={open} onClose={() => setOpen(false)} projectId={projectId} lotId={lotId} vueAngle={vueAngle} busy={busy} onConfirm={run} />
    </>
  );
};

export default DeterministicWhiteboxButton;
